<template>
  <div class="help-root">
    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">security</span>
      <p>
        La autenticación de dos factores (MFA) añade una capa adicional de
        seguridad a tu cuenta. Además de tu contraseña, necesitarás un código
        de 6 dígitos generado por tu aplicación autenticadora cada vez que
        inicies sesión.
      </p>
    </div>

    <!-- ¿Qué es MFA? -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">help</span>
        ¿Qué es la autenticación de dos factores?
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in conceptos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Estados -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">info</span>
        Estados
      </h3>
      <div class="help-estados">
        <span class="help-estado-badge" style="background:#d4edda;color:#155724">Activo</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Inactivo</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem;margin-top:0.5rem">
        Cuando MFA está <strong>Activo</strong>, el sistema solicitará un
        código de verificación en cada inicio de sesión. Cuando está
        <strong>Inactivo</strong>, solo se requiere usuario y contraseña.
      </p>
    </section>

    <!-- Aplicaciones compatibles -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">smartphone</span>
        Aplicaciones compatibles
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="app in aplicaciones" :key="app.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ app.icon }}</span>
          <div>
            <p class="help-card-title">{{ app.titulo }}</p>
            <p class="help-card-desc">{{ app.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pasos para activar -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">checklist</span>
        Cómo activar MFA
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="paso in pasosActivar" :key="paso.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: paso.color }">{{ paso.icon }}</span>
          <div>
            <p class="help-card-title">{{ paso.titulo }}</p>
            <p class="help-card-desc">{{ paso.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Acciones disponibles -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Preguntas frecuentes -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">quiz</span>
        Preguntas frecuentes
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="faq in faqs" :key="faq.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ faq.icon }}</span>
          <div>
            <p class="help-card-title">{{ faq.titulo }}</p>
            <p class="help-card-desc">{{ faq.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        El código QR y la clave de configuración son equivalentes — puedes
        usar cualquiera de los dos para agregar tu cuenta en la aplicación
        autenticadora. El código cambia cada <strong>30 segundos</strong>,
        así que ingrésalo rápidamente una vez que lo veas en tu teléfono.
        Si pierdes acceso a tu aplicación autenticadora, contacta al
        administrador del sistema para desactivar MFA en tu cuenta.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: "AyudaMfa",
  data() {
    return {
      conceptos: [
        {
          icon:   "password",
          titulo: "Factor 1 — Contraseña",
          desc:   "Tu contraseña habitual que solo tú conoces.",
        },
        {
          icon:   "smartphone",
          titulo: "Factor 2 — Código TOTP",
          desc:   "Un código de 6 dígitos que se genera en tu teléfono y cambia cada 30 segundos.",
        },
        {
          icon:   "verified_user",
          titulo: "Mayor seguridad",
          desc:   "Aunque alguien obtenga tu contraseña, no podrá ingresar sin el código de tu teléfono.",
        },
      ],
      aplicaciones: [
        {
          icon:   "apps",
          titulo: "Google Authenticator",
          desc:   "Disponible en Android e iOS. Sencilla y confiable, no requiere conexión a internet para generar códigos.",
        },
        {
          icon:   "security",
          titulo: "Microsoft Authenticator",
          desc:   "Disponible en Android e iOS. Permite respaldo en la nube para recuperar tus cuentas si cambias de teléfono.",
        },
        {
          icon:   "lock",
          titulo: "Authy",
          desc:   "Disponible en Android, iOS y escritorio. Soporta múltiples dispositivos y respaldo cifrado.",
        },
      ],
      pasosActivar: [
        {
          icon:   "download",
          color:  "#0059cc",
          titulo: "Paso 1 — Instala la aplicación",
          desc:   "Descarga Google Authenticator, Microsoft Authenticator o Authy en tu teléfono móvil.",
        },
        {
          icon:   "add_moderator",
          color:  "#198754",
          titulo: "Paso 2 — Haz clic en Activar MFA",
          desc:   "Presiona el botón 'Activar MFA' en esta pantalla. El sistema generará un código QR exclusivo para tu cuenta.",
        },
        {
          icon:   "qr_code_2",
          color:  "#6f42c1",
          titulo: "Paso 3 — Escanea el código QR",
          desc:   "Abre tu aplicación autenticadora, toca el botón '+' y selecciona 'Escanear código QR'. Apunta la cámara al QR que aparece en pantalla.",
        },
        {
          icon:   "pin",
          color:  "#fd7e14",
          titulo: "Paso 4 — Confirma con el primer código",
          desc:   "Ingresa el código de 6 dígitos que muestra la aplicación para confirmar que el escaneo fue exitoso.",
        },
        {
          icon:   "verified",
          color:  "#198754",
          titulo: "Paso 5 — MFA activado",
          desc:   "A partir de este momento, cada inicio de sesión pedirá tu contraseña y el código de tu aplicación.",
        },
      ],
      acciones: [
        {
          icon:   "add_moderator",
          color:  "#0059cc",
          titulo: "Activar MFA",
          desc:   "Inicia el proceso de configuración. Genera un QR exclusivo para tu cuenta que debes escanear con tu aplicación autenticadora.",
        },
        {
          icon:   "qr_code_2",
          color:  "#6f42c1",
          titulo: "Escanear QR",
          desc:   "Muestra el código QR y la clave manual de configuración. Puedes copiar la clave si no puedes escanear el QR.",
        },
        {
          icon:   "content_copy",
          color:  "#6c757d",
          titulo: "Copiar clave manual",
          desc:   "Copia la clave de configuración al portapapeles para ingresarla manualmente en la aplicación autenticadora.",
        },
        {
          icon:   "remove_moderator",
          color:  "#dc3545",
          titulo: "Desactivar MFA",
          desc:   "Desactiva la autenticación de dos factores. Tu cuenta quedará protegida solo por contraseña. Se solicitará confirmación antes de proceder.",
        },
      ],
      faqs: [
        {
          icon:   "mobile",
          titulo: "¿Qué pasa si cambio de teléfono?",
          desc:   "Debes volver a configurar MFA en tu nuevo teléfono. Desactiva MFA desde esta pantalla, luego vuelve a activarlo escaneando un nuevo QR.",
        },
        {
          icon:   "mobile_off",
          titulo: "¿Qué pasa si pierdo mi teléfono?",
          desc:   "Contacta al administrador del sistema para que desactive MFA en tu cuenta y puedas ingresar solo con tu contraseña.",
        },
        {
          icon:   "schedule",
          titulo: "¿Por qué el código no funciona?",
          desc:   "El código cambia cada 30 segundos. Asegúrate de ingresarlo antes de que expire. También verifica que la hora de tu teléfono esté sincronizada correctamente.",
        },
        {
          icon:   "key",
          titulo: "¿Puedo usar la clave manual en lugar del QR?",
          desc:   "Sí. En tu aplicación autenticadora selecciona 'Ingresar clave manualmente' y escribe la clave que aparece debajo del QR.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>